import React, { useMemo, useState } from "react";
import MultiBranchSelect from "../../../../../features/MultiBranchSelect";
import { useSelector } from "react-redux";
import { getTodayMonth } from '../../../../../utils/Date/SupportDate';
import { coverIDToFullName } from '../../../../../utils/Branch/SupportBranch';
import RoomReportTotalAreaBranchDetailsDay from "./RoomReportTotalAreaBranchDetailsDay";
import './roomReportTotalExecutive.css';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    BarElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    ArcElement,
    BarElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const OTA_COLORS = {
    "agoda": "#4285F4",
    "booking.com": "#DB4437",
    "trip.com": "#F4B400",
    "expedia": "#0F9D58",
    "rakuten": "#4285F4",
    "hafh": "#DB4437"
};

const GOOGLE_PALETTE = ["#4285F4", "#DB4437", "#F4B400", "#0F9D58"];
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatMonthLabel(month) {
    const [yearText, monthText] = String(month || "").split("-");
    const year = Number(yearText);
    const monthNumber = Number(monthText);

    if (!year || !monthNumber || !MONTH_LABELS[monthNumber - 1]) return "Previous month";
    return `${MONTH_LABELS[monthNumber - 1]} ${year}`;
}

function formatCount(value) {
    return new Intl.NumberFormat("en-US").format(Number(value) || 0);
}

function formatPercent(value) {
    const numericValue = Number(value) || 0;
    return `${Math.round(numericValue * 100) / 100}%`;
}

function formatOtaLabel(channel) {
    return String(channel || "")
        .split('.')
        .map(part => part ? part.charAt(0).toUpperCase() + part.slice(1) : part)
        .join('.');
}

function RoomReportTotalAreaBranchByGemini(props) {
    const [dataBranch, setDataBranch] = useState({
        area: "Select Area",
        branch: "Select Branch",
        branchID: 0,
        month: getTodayMonth()
    });
    const [modalInfo, setModalInfo] = useState(null);
    const [tableSort, setTableSort] = useState({ type: "", key: "", direction: "desc" });
    const [yearMode, setYearMode] = useState(false);

    const auth = useSelector(state => state.auth);
    const allBranchInform = useSelector(state => state.allBranchInformation);
    const reportTotalRedux = useSelector(state => state.reportTotal);
    const { viewRoom } = reportTotalRedux;

    const selectedYear = useMemo(() => {
        const match = String(dataBranch.month || "").match(/^(\d{4})/);
        return match ? match[1] : new Date().getFullYear();
    }, [dataBranch.month]);

    const { role, area } = auth.user;
    const isMultiBranch = (role === 5 || role === 6 || role === 7 || role === 8 || role === 1);
    const isMultiArea = (role === 7 || role === 1 || role === 8);

    const listArea = allBranchInform === undefined ? [] : Object.keys(allBranchInform);
    const listBranch = (role === 6) ? renderListBranch(area) : (role === 7) ? renderListBranch(dataBranch.area) : [];

    function renderListBranch(areaValue) {
        if (JSON.stringify(allBranchInform) !== '{}' && areaValue !== "Select Area") {
            return allBranchInform[areaValue]?.map(b => b.name) || [];
        }
        return [];
    }

    function onChangeData(target, value, index) {
        const newData = { ...dataBranch };
        if (target === "branch") {
            props.resetDataWithTarget("viewRoom", "REPORT BRANCH");    
            const checkArea = isMultiArea ? dataBranch.area : area;
            newData.branchID = allBranchInform[checkArea][index].branchID;
            newData[target] = value;
        } else if (target === "month") {
            if (viewRoom.content.length > 0) props.resetDataWithTarget("viewRoom", "REPORT BRANCH");
            newData[target] = value;
        } else {
            newData[target] = value;
        }
        setDataBranch(newData);
    }

    function getReportPeriodValue() {
        if (!yearMode) return dataBranch.month;
        return selectedYear;
    }

    function handleLoadData() {
        const reportValue = getReportPeriodValue();
        props.onLoadData(
            dataBranch.branchID,
            isMultiArea ? dataBranch.area.replace(/ /g, "_") : area,
            reportValue
        );
    }

    function toggleYearMode(nextMode) {
        setYearMode(nextMode);
        if (viewRoom.content.length > 0) props.resetDataWithTarget("viewRoom", "REPORT BRANCH");
    }

    function branchUseOTA() {
        if (Number(dataBranch.branchID) === 0) {
            const areaChannels = Object.keys(viewRoom.OTATotalDetails || {}).filter(Boolean);
            if (areaChannels.length > 0) return areaChannels;
        }

        switch(dataBranch.branchID) {
            case 1: case 4: case 9:  return ["agoda", "trip.com", "hafh"];
            case 3: case 8: case 12: case 16: return ["expedia", "booking.com", "agoda"];
            case 13: case 7: return ["expedia", "booking.com", "agoda", "rakuten", "hafh"];
            case 11: return ["expedia", "agoda", "trip.com"];
            case 15: return ["expedia", "booking.com", "agoda", "trip.com"];
            default: return ["expedia", "booking.com", "agoda"];
        }
    }

    const isAreaSummaryReport = String(viewRoom?.reportScope || "") === "AREA" || String(viewRoom?.branchName || "").includes("All Branches");
    const headerTargetName = isAreaSummaryReport
        ? (viewRoom.branchName || `${dataBranch.area} - All Branches`)
        : (coverIDToFullName(dataBranch.branchID) || dataBranch.branch || "");
    const previousMonthSummary = viewRoom?.previousMonthSummary || null;
    const previousMonthLabel = formatMonthLabel(previousMonthSummary?.reportMonth);

    // Chart Options & Data giống ảnh thiết kế mẫu
    const doughnutOptions = {
        cutout: '70%',
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true }
        },
        maintainAspectRatio: false
    };

    const otaBreakdown = branchUseOTA()
        .map(channel => ({
            channel,
            value: viewRoom.OTATotalDetails?.[channel] || 0,
            color: OTA_COLORS[channel] || "#94a3b8"
        }))
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value);

    const otaChartData = {
        labels: otaBreakdown.map(item => formatOtaLabel(item.channel)),
        datasets: [{
            data: otaBreakdown.map(item => item.value),
            backgroundColor: otaBreakdown.map(item => item.color),
            borderWidth: 0,
        }]
    };

    const otaMonthlyByBranch = useMemo(() => {
        const details = Array.isArray(viewRoom.areaBranchDetails) ? viewRoom.areaBranchDetails : [];
        const yearText = String(selectedYear || "").match(/^(\d{4})/)?.[1];
        if (!yearText || details.length === 0) return { months: [], rows: [] };

        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;
        const maxMonth = Number(yearText) < currentYear ? 12 : currentMonth;
        const months = MONTH_LABELS.slice(0, maxMonth);

        const rows = details.map((branch) => {
            const byMonth = Array.from({ length: maxMonth }).map(() => 0);

            (branch?.monthlyOta || []).forEach((item) => {
                const rawMonth = String(item?.month || "");
                const match = rawMonth.match(/^(\d{4})[-/](\d{1,2})$/);
                if (!match) return;

                const y = match[1];
                const m = Number(match[2]);
                if (y !== yearText || !m || m < 1 || m > maxMonth) return;
                byMonth[m - 1] = Number(item?.otaTotal) || 0;
            });

            const total = byMonth.reduce((sum, value) => sum + (Number(value) || 0), 0);
            return {
                branchName: branch?.branchName || "-",
                areaName: branch?.areaName || "-",
                byMonth,
                total,
            };
        });

        return { months, rows };
    }, [viewRoom.areaBranchDetails, selectedYear]);

    const customerTypeData = {
        labels: ["Company", "OTA", "Others"],
        datasets: [{
            data: [
                viewRoom.companyTotal || 0,
                // viewRoom.memberShipTotal || 0,
                viewRoom.OTATotal || 0,
                (viewRoom.roomTotal || 0) - ((viewRoom.companyTotal || 0)  + (viewRoom.OTATotal || 0))
            ],
            backgroundColor: ['#4285F4', '#F4B400', '#0F9D58'],
            borderWidth: 0,
        }]
    };

    const previousMonthOccupiedSubText = useMemo(() => {
        if (!previousMonthSummary) return "";

        const roomTotal = Number(previousMonthSummary.roomTotal || 0);
        const companyTotal = Number(previousMonthSummary.companyTotal || 0);
        const otaTotal = Number(previousMonthSummary.OTATotal || 0);
        const othersTotal = roomTotal - (companyTotal + otaTotal);

        return ` ${previousMonthLabel}: ${formatCount(roomTotal)} nights (${formatPercent(previousMonthSummary.capacity || 0)}) ( Company ${formatCount(companyTotal)} | OTA ${formatCount(otaTotal)} | Others ${formatCount(othersTotal)})`;
    }, [previousMonthLabel, previousMonthSummary]);

    const previousMonthOtaSubText = useMemo(() => {
        if (!previousMonthSummary) return "";

        const previousRoomTotal = Number(previousMonthSummary.roomTotal || 0);
        const previousOtaTotal = Number(previousMonthSummary.OTATotal || 0);
        const previousOtaRate = previousRoomTotal > 0 ? (previousOtaTotal / previousRoomTotal) * 100 : 0;
        const previousOtaDetails = previousMonthSummary.OTATotalDetails || {};
        const previousOtaChannels = branchUseOTA()
            .map((channel) => ({
                channel,
                value: Number(previousOtaDetails?.[channel] || 0),
            }))
            .filter((item) => item.value > 0)
            .sort((a, b) => b.value - a.value)
            .slice(0, 3)
            .map((item) => `${formatOtaLabel(item.channel)} ${formatCount(item.value)}`)
            .join(" | ");

        return ` ${previousMonthLabel}: ${formatCount(previousOtaTotal)} nights (${formatPercent(previousOtaRate)})${previousOtaChannels ? ` | ${previousOtaChannels}` : ""}`;
    }, [previousMonthLabel, previousMonthSummary, dataBranch.branchID, viewRoom.OTATotalDetails]);

    const serviceItems = [
        { key: "totalBF", label: "Breakfast", value: Number(viewRoom.totalBF) || 0, color: "#4285F4" },
        { key: "totalRoten", label: "Rotenburo", value: Number(viewRoom.totalRoten) || 0, color: "#0F9D58" },
        { key: "totalMassage", label: "Massage", value: Number(viewRoom.totalMassage) || 0, color: "#DB4437" },
    ];
    const maxServiceValue = Math.max(...serviceItems.map(item => item.value), 0);

    const fallbackAreaCapacityDatasets = useMemo(() => {
        const branchDetails = Array.isArray(viewRoom.areaBranchDetails) ? viewRoom.areaBranchDetails : [];
        const selectedYearText = String(selectedYear || "");

        return branchDetails
            .map((branch, index) => {
                const points = Array.from({ length: 12 }).map(() => 0);
                let hasData = false;

                (branch.monthly || []).forEach((m) => {
                    const rawMonth = String(m?.month || "");
                    const match = rawMonth.match(/^(\d{4})[-/](\d{1,2})$/);
                    if (!match) return;

                    const yearText = match[1];
                    const monthNumber = Number(match[2]);
                    if (!monthNumber || monthNumber < 1 || monthNumber > 12) return;

                    if (yearMode && selectedYearText && yearText !== selectedYearText) return;

                    points[monthNumber - 1] = Number(m?.capacity) || 0;
                    hasData = true;
                });

                if (!hasData) return null;

                return {
                    label: branch?.branchName || `Branch ${index + 1}`,
                    data: points,
                };
            })
            .filter(Boolean);
    }, [viewRoom.areaBranchDetails, yearMode, selectedYear]);

    const areaComparisonData = useMemo(() => {
        const sourceFromApi = Array.isArray(viewRoom.dataCapacityMonthArea) ? viewRoom.dataCapacityMonthArea : [];
        const hasUsableApiData = sourceFromApi.some((dataset) =>
            Array.isArray(dataset?.data) && dataset.data.some((value) => Number(value) > 0)
        );
        const hasFallbackData = fallbackAreaCapacityDatasets.some((dataset) =>
            Array.isArray(dataset?.data) && dataset.data.some((value) => Number(value) > 0)
        );
        const source = hasUsableApiData || !hasFallbackData ? sourceFromApi : fallbackAreaCapacityDatasets;
        const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        const barDatasets = source.map((dataset, index) => ({
            ...dataset,
            type: "bar",
            backgroundColor: GOOGLE_PALETTE[index % GOOGLE_PALETTE.length],
            borderColor: GOOGLE_PALETTE[index % GOOGLE_PALETTE.length],
            borderWidth: 0,
            borderRadius: 8,
            borderSkipped: false,
            maxBarThickness: 24,
        }));

        const maxLen = Math.max(
            0,
            ...barDatasets.map(ds => Array.isArray(ds.data) ? ds.data.length : 0)
        );

        const avgData = Array.from({ length: maxLen }).map((_, i) => {
            const values = barDatasets
                .map(ds => Number(ds?.data?.[i]))
                .filter(v => Number.isFinite(v));
            if (!values.length) return 0;
            return Math.round((values.reduce((sum, v) => sum + v, 0) / values.length) * 10) / 10;
        });

        return {
            labels,
            datasets: [
                ...barDatasets,
                {
                    type: "line",
                    label: "Average",
                    data: avgData,
                    borderColor: "#DB4437",
                    backgroundColor: "#DB4437",
                    pointBackgroundColor: "#ffffff",
                    pointBorderColor: "#DB4437",
                    pointBorderWidth: 2,
                    pointRadius: 3,
                    pointHoverRadius: 5,
                    borderWidth: 2.5,
                    tension: 0.3,
                }
            ],
        };
    }, [viewRoom.dataCapacityMonthArea, fallbackAreaCapacityDatasets]);

    const areaComparisonOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    boxWidth: 10,
                    padding: 14,
                }
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label || ''}: ${ctx.parsed.y ?? 0}%`
                }
            }
        },
        scales: {
            x: {
                grid: { display: false }
            },
            y: {
                beginAtZero: true,
                suggestedMax: 100,
                ticks: {
                    callback: (value) => `${value}%`
                },
                grid: {
                    color: 'rgba(148, 163, 184, 0.22)'
                }
            }
        },
        animation: {
            duration: 850,
            easing: 'easeOutQuart'
        }
    };

    const monthLabels = MONTH_LABELS;

    function openReportModal(payload) {
        setModalInfo(payload);
    }

    function closeReportModal() {
        setModalInfo(null);
    }

    function onSortTable(type, key) {
        setTableSort((prev) => {
            if (prev.type === type && prev.key === key) {
                return {
                    ...prev,
                    direction: prev.direction === "desc" ? "asc" : "desc"
                };
            }
            return { type, key, direction: "desc" };
        });
    }

    function renderSortableHeader(type, key, label) {
        const isActive = tableSort.type === type && tableSort.key === key;
        const icon = isActive ? (tableSort.direction === "desc" ? " ↓" : " ↑") : "";

        return (
            <th
                onClick={() => onSortTable(type, key)}
                style={{ cursor: "pointer", userSelect: "none" }}
            >
                {label}{icon}
            </th>
        );
    }

    function valueBySortKey(branch, key, detailType) {
        if (key === "area") return String(branch?.areaName || "").toLowerCase();
        if (key === "branch") return String(branch?.branchName || "").toLowerCase();
        if (key.startsWith("monthly_ota:")) {
            const monthIndex = Number(key.replace("monthly_ota:", ""));
            return Number(branch?.byMonth?.[monthIndex] || 0);
        }
        if (key === "others") {
            return Number(branch?.roomTotal || 0) - (Number(branch?.companyTotal || 0) + Number(branch?.OTATotal || 0));
        }
        if (key === "ota_total") return Number(branch?.OTATotal || 0);
        if (key.startsWith("ota_channel:")) {
            const channel = key.replace("ota_channel:", "");
            return Number(branch?.OTATotalDetails?.[channel] || 0);
        }
        if (key === "monthly_avg_capacity") {
            const monthly = Array.isArray(branch?.monthly) ? branch.monthly : [];
            if (monthly.length === 0) return 0;
            const sum = monthly.reduce((acc, item) => acc + (Number(item?.capacity) || 0), 0);
            return sum / monthly.length;
        }

        if (key === "kpi_col_1") {
            if (detailType === "female") return Number(branch?.femaleTotal || 0);
            if (detailType === "membership") return Number(branch?.memberShipTotal || 0);
            if (detailType === "appweb") return Number(branch?.fromAzAppTotal || 0);
            if (detailType === "car") return Number(branch?.pickUpTotal || 0);
            if (detailType === "cancel_waiting") return Number(branch?.cancelListTotal || 0);
            if (detailType === "survey") return Number(branch?.surveyTotal || 0);
            if (detailType === "line_coupon") return Number(branch?.totalRoomLine || 0);
            return Number(branch?.roomTotal || 0);
        }
        if (key === "kpi_col_2") {
            if (detailType === "female" || detailType === "membership") return Number(branch?.roomTotal || 0);
            if (detailType === "appweb" ) return Number(branch?.fromAzWebTotal || 0);
            if (detailType === "car") return Number(branch?.dropOffTotal || 0);
            if (detailType === "cancel_waiting") return Number(branch?.waitingListTotal || 0);
            return 0;
        }
        if (key === "kpi_col_3") {
            if (detailType === "female") {
                const roomTotal = Number(branch?.roomTotal || 0);
                return roomTotal > 0 ? (Number(branch?.femaleTotal || 0) / roomTotal) * 100 : 0;
            }
            if (detailType === "membership") {
                const roomTotal = Number(branch?.roomTotal || 0);
                return roomTotal > 0 ? (Number(branch?.memberShipTotal || 0) / roomTotal) * 100 : 0;
            }
            if (detailType === "appweb") return Number(branch?.fromAzAppTotal || 0) + Number(branch?.fromAzWebTotal || 0);
            if (detailType === "car") return Number(branch?.pickUpTotal || 0) + Number(branch?.dropOffTotal || 0);
            if (detailType === "cancel_waiting") return Number(branch?.cancelListTotal || 0) + Number(branch?.waitingListTotal || 0);
            return 0;
        }

        const raw = branch?.[key];
        if (typeof raw === "number") return raw;
        if (raw === null || raw === undefined) return "";
        if (typeof raw === "string") {
            const n = Number(raw);
            if (!Number.isNaN(n) && raw.trim() !== "") return n;
            return raw.toLowerCase();
        }
        return raw;
    }

    function sortBranchDetails(branchDetails, type, detailType) {
        if (tableSort.type !== type || !tableSort.key) return branchDetails;

        const sorted = [...branchDetails].sort((a, b) => {
            const valueA = valueBySortKey(a, tableSort.key, detailType);
            const valueB = valueBySortKey(b, tableSort.key, detailType);

            if (typeof valueA === "number" && typeof valueB === "number") {
                return valueA - valueB;
            }
            return String(valueA).localeCompare(String(valueB));
        });

        return tableSort.direction === "desc" ? sorted.reverse() : sorted;
    }

    function renderModalBody() {
        if (!modalInfo) return null;
        const branchDetails = Array.isArray(viewRoom.areaBranchDetails) ? viewRoom.areaBranchDetails : [];

        if (modalInfo.type === "customer") {
            if (branchDetails.length > 0) {
                const sortedDetails = sortBranchDetails(branchDetails, "customer");
                return (
                    <div className="modal-table-wrap">
                        <table className="modal-table">
                            <thead>
                                <tr>
                                    {renderSortableHeader("customer", "branch", "Branch")}
                                    {renderSortableHeader("customer", "area", "Area")}
                                    {renderSortableHeader("customer", "companyTotal", "Company")}
                                    {renderSortableHeader("customer", "OTATotal", "OTA")}
                                    {renderSortableHeader("customer", "others", "Others")}
                                    {renderSortableHeader("customer", "roomTotal", "Room Total")}
                                    {renderSortableHeader("customer", "capacity", "Capacity")}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedDetails.map((branch) => {
                                    const company = Number(branch.companyTotal || 0);
                                    const ota = Number(branch.OTATotal || 0);
                                    const roomTotal = Number(branch.roomTotal || 0);
                                    const others = roomTotal - (company + ota);
                                    const capacity = Number(branch.capacity || 0);

                                    return (
                                        <tr key={`${branch.branchID}-${branch.branchName}`}>
                                            <td>{branch.branchName}</td>
                                            <td>{branch.areaName || "-"}</td>
                                            <td>{company}</td>
                                            <td>{ota}</td>
                                            <td>{others}</td>
                                            <td>{roomTotal}</td>
                                            <td>{capacity.toFixed(2)}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            }

            const rows = [
                { label: "Company", value: viewRoom.companyTotal || 0, color: "#4285F4" },
                { label: "OTA", value: viewRoom.OTATotal || 0, color: "#F4B400" },
                { label: "Others", value: (viewRoom.roomTotal || 0) - ((viewRoom.companyTotal || 0) + (viewRoom.OTATotal || 0)), color: "#0F9D58" },
            ];

            return (
                <div className="modal-detail-list">
                    {rows.map((row) => (
                        <div key={row.label} className="modal-detail-row">
                            <span className="dot" style={{ background: row.color }} />
                            <span>{row.label}</span>
                            <strong>{row.value} nights</strong>
                        </div>
                    ))}
                </div>
            );
        }

        if (modalInfo.type === "ota") {
            if (branchDetails.length > 0) {
                const channelSet = new Set();
                branchDetails.forEach((b) => {
                    Object.keys(b.OTATotalDetails || {}).forEach((k) => channelSet.add(k));
                });
                const channels = Array.from(channelSet).sort();
                const sortedDetails = sortBranchDetails(branchDetails, "ota");

                return (
                    <div>
                        <div className="modal-table-wrap">
                            <table className="modal-table">
                                <thead>
                                    <tr>
                                        {renderSortableHeader("ota", "branch", "Branch")}
                                        {renderSortableHeader("ota", "area", "Area")}
                                        {channels.map((c) => (
                                            <React.Fragment key={c}>
                                                {renderSortableHeader("ota", `ota_channel:${c}`, formatOtaLabel(c))}
                                            </React.Fragment>
                                        ))}
                                        {renderSortableHeader("ota", "ota_total", "OTA Total")}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedDetails.map((branch) => (
                                        <tr key={`${branch.branchID}-${branch.branchName}`}>
                                            <td>{branch.branchName}</td>
                                            <td>{branch.areaName || "-"}</td>
                                            {channels.map((c) => <td key={c}>{Number(branch.OTATotalDetails?.[c] || 0)}</td>)}
                                            <td>{Number(branch.OTATotal || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {yearMode && otaMonthlyByBranch.rows.length > 0 ? (
                            <div className="modal-table-wrap mt-3">
                                <div style={{ fontWeight: 700, marginBottom: "8px", color: "#5789ff", textAlign: "justify", fontSize: "1.2rem" }}>
                                    * OTA Monthly Total by Branch ({selectedYear})
                                </div>
                                <table className="modal-table">
                                    <thead>
                                        <tr>
                                            {renderSortableHeader("otaMonthly", "branch", "Branch")}
                                            {renderSortableHeader("otaMonthly", "area", "Area")}
                                            {otaMonthlyByBranch.months.map((month, monthIndex) => (
                                                <React.Fragment key={month}>
                                                    {renderSortableHeader("otaMonthly", `monthly_ota:${monthIndex}`, month)}
                                                </React.Fragment>
                                            ))}
                                            {renderSortableHeader("otaMonthly", "total", "Total")}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortBranchDetails(otaMonthlyByBranch.rows, "otaMonthly").map((row) => (
                                            <tr key={`${row.areaName}-${row.branchName}`}>
                                                <td>{row.branchName}</td>
                                                <td>{row.areaName}</td>
                                                {row.byMonth.map((value, idx) => (
                                                    <td key={idx}>{formatCount(value)}</td>
                                                ))}
                                                <td>{formatCount(row.total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : null}
                    </div>
                );
            }

            return (
                <div className="modal-detail-list">
                    {otaBreakdown.length > 0 ? otaBreakdown.map((item) => (
                        <div key={item.channel} className="modal-detail-row">
                            <span className="dot" style={{ background: item.color }} />
                            <span>{formatOtaLabel(item.channel)}</span>
                            <strong>{item.value} nights</strong>
                        </div>
                    )) : <div>Không có dữ liệu OTA.</div>}
                </div>
            );
        }

        if (modalInfo.type === "service") {
            if (branchDetails.length > 0) {
                const sortedDetails = sortBranchDetails(branchDetails, "service");
                return (
                    <div className="modal-table-wrap">
                        <table className="modal-table">
                            <thead>
                                <tr>
                                    {renderSortableHeader("service", "branch", "Branch")}
                                    {renderSortableHeader("service", "area", "Area")}
                                    {renderSortableHeader("service", "totalBF", "Breakfast")}
                                    {renderSortableHeader("service", "totalRoten", "Rotenburo")}
                                    {renderSortableHeader("service", "totalMassage", "Massage")}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedDetails.map((branch) => (
                                    <tr key={`${branch.branchID}-${branch.branchName}`}>
                                        <td>{branch.branchName}</td>
                                        <td>{branch.areaName || "-"}</td>
                                        <td>{Number(branch.totalBF || 0)}</td>
                                        <td>{Number(branch.totalRoten || 0)}</td>
                                        <td>{Number(branch.totalMassage || 0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            }

            return (
                <div className="modal-detail-list">
                    {serviceItems.map((item) => (
                        <div key={item.key} className="modal-detail-row">
                            <span className="dot" style={{ background: item.color }} />
                            <span>{item.label}</span>
                            <strong>{item.value}</strong>
                        </div>
                    ))}
                </div>
            );
        }

        if (modalInfo.type === "kpi") {
            const item = modalInfo.item || {};

            if (branchDetails.length > 0) {
                const sortedDetails = sortBranchDetails(branchDetails, "kpi", item.detailType);
                const headers = {
                    female: ["Female", "Room Total", "Female %"],
                    membership: ["Membership", "Room Total", "Membership %"],
                    appweb: ["Az App", "Az Web", "Total"],
                    car: ["Pick Up", "Drop Off", "Total"],
                    cancel_waiting: ["Cancel", "Waiting", "Total"],
                    survey: ["Survey", "", ""],
                    line_coupon: ["Line Coupon", "", ""],
                };

                const mapRows = {
                    female: (b) => {
                        const female = Number(b.femaleTotal || 0);
                        const roomTotal = Number(b.roomTotal || 0);
                        const ratio = roomTotal > 0 ? Math.round((female / roomTotal) * 100) : 0;
                        return [female, roomTotal, `${ratio}%`];
                    },
                    membership: (b) => {
                        const member = Number(b.memberShipTotal || 0);
                        const roomTotal = Number(b.roomTotal || 0);
                        const ratio = roomTotal > 0 ? Math.round((member / roomTotal) * 100) : 0;
                        return [member, roomTotal, `${ratio}%`];
                    },
                    appweb: (b) => {
                        const app = Number(b.fromAzAppTotal || 0);
                        const web = Number(b.fromAzWebTotal || 0);
                        return [app, web, app + web];
                    },
                    car: (b) => {
                        const pick = Number(b.pickUpTotal || 0);
                        const drop = Number(b.dropOffTotal || 0);
                        return [pick, drop, pick + drop];
                    },
                    cancel_waiting: (b) => {
                        const cancel = Number(b.cancelListTotal || 0);
                        const wait = Number(b.waitingListTotal || 0);
                        return [cancel, wait, cancel + wait];
                    },
                    survey: (b) => [Number(b.surveyTotal || 0), "", ""],
                    line_coupon: (b) => [Number(b.totalRoomLine || 0), "", ""],
                };

                const header = headers[item.detailType] || ["Value", "", ""];
                const rowBuilder = mapRows[item.detailType] || ((b) => [Number(b.roomTotal || 0), "", ""]);

                return (
                    <div className="modal-table-wrap">
                        <table className="modal-table">
                            <thead>
                                <tr>
                                    {renderSortableHeader("kpi", "branch", "Branch")}
                                    {renderSortableHeader("kpi", "area", "Area")}
                                    {header[0] ? renderSortableHeader("kpi", "kpi_col_1", header[0]) : null}
                                    {header[1] ? renderSortableHeader("kpi", "kpi_col_2", header[1]) : null}
                                    {header[2] ? renderSortableHeader("kpi", "kpi_col_3", header[2]) : null}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedDetails.map((branch) => {
                                    const cells = rowBuilder(branch);
                                    return (
                                        <tr key={`${branch.branchID}-${branch.branchName}`}>
                                            <td>{branch.branchName}</td>
                                            <td>{branch.areaName || "-"}</td>
                                            {header[0] ? <td>{cells[0]}</td> : null}
                                            {header[1] ? <td>{cells[1]}</td> : null}
                                            {header[2] ? <td>{cells[2]}</td> : null}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            }

            return (
                <div className="modal-kpi-box">
                    <div className="modal-kpi-value">{item.value}{item.unit || ""}</div>
                    <div className="modal-kpi-goal">{item.goal || ""}</div>
                    {item.subText ? <div className="modal-kpi-sub">{item.subText}</div> : null}
                </div>
            );
        }

        if (modalInfo.type === "areaComparison") {
            if (branchDetails.length > 0) {
                const monthSet = new Set();
                branchDetails.forEach((b) => {
                    (b.monthly || []).forEach((m) => {
                        if (m?.month) monthSet.add(m.month);
                    });
                });

                const months = Array.from(monthSet).sort();
                const sortedDetails = sortBranchDetails(branchDetails, "areaComparison");

                return (
                    <div className="modal-table-wrap">
                        <table className="modal-table">
                            <thead>
                                <tr>
                                    {renderSortableHeader("areaComparison", "branch", "Branch")}
                                    {renderSortableHeader("areaComparison", "area", "Area")}
                                    {months.map((m) => <th key={m}>{m}</th>)}
                                    {/* {renderSortableHeader("areaComparison", "capacity", "Capacity")} */}
                                    {renderSortableHeader("areaComparison", "monthly_avg_capacity", "Avg Month Capacity")}
                                    {/* {renderSortableHeader("areaComparison", "roomTotal", "Room Total")}
                                    {renderSortableHeader("areaComparison", "ota_total", "OTA Total")} */}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedDetails.map((branch) => (
                                    <tr key={`${branch.branchID}-${branch.branchName}`}>
                                        <td>{branch.branchName}</td>
                                        <td>{branch.areaName || "-"}</td>
                                        {months.map((m) => {
                                            const found = (branch.monthly || []).find((x) => x.month === m);
                                            return <td key={m}>{found ? `${Number(found.capacity || 0).toFixed(2)}%` : "—"}</td>;
                                        })}
                                        {/* <td>{Number(branch.capacity || 0).toFixed(2)}%</td> */}
                                        <td>{Number(valueBySortKey(branch, "monthly_avg_capacity") || 0).toFixed(2)}%</td>
                                        {/* <td>{branch.roomTotal || 0}</td>
                                        <td>{branch.OTATotal || 0}</td> */}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            }
        }

        const sourceFromApi = Array.isArray(viewRoom.dataCapacityMonthArea) ? viewRoom.dataCapacityMonthArea : [];
        const hasUsableApiData = sourceFromApi.some((dataset) =>
            Array.isArray(dataset?.data) && dataset.data.some((value) => Number(value) > 0)
        );
        const hasFallbackData = fallbackAreaCapacityDatasets.some((dataset) =>
            Array.isArray(dataset?.data) && dataset.data.some((value) => Number(value) > 0)
        );
        const areaDatasets = hasUsableApiData || !hasFallbackData ? sourceFromApi : fallbackAreaCapacityDatasets;
        const maxMonths = Math.max(0, ...areaDatasets.map((d) => Array.isArray(d.data) ? d.data.length : 0));

        return (
            <div className="modal-table-wrap">
                {areaDatasets.length === 0 ? (
                    <div>Không có dữ liệu khu vực.</div>
                ) : (
                    <table className="modal-table">
                        <thead>
                            <tr>
                                <th>Area/Branch</th>
                                {monthLabels.slice(0, maxMonths).map((m) => <th key={m}>{m}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {areaDatasets.map((dataset, i) => (
                                <tr key={`${dataset.label}-${i}`}>
                                    <td>{dataset.label}</td>
                                    {monthLabels.slice(0, maxMonths).map((_, idx) => (
                                        <td key={idx}>{Number(dataset?.data?.[idx] || 0).toFixed(2)}%</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        );
    }

    return (
        <div className="dashboard-container w-100">
            {/* Control Bar Header */}
            <div className="dashboard-header">
                <div className="dashboard-filters">
                    <MultiBranchSelect
                        listArea={listArea}
                        listBranch={listBranch}
                        changeSelectArea={(v, i) => onChangeData("area", v, i)}
                        changeSelectBranchID={(v, i) => onChangeData("branch", v, i)}
                        selectArea={dataBranch.area}
                        selectBranch={dataBranch.branch}
                    />
                    <input
                        type="month"
                        className="form-control form-control-sm"
                        style={{ width: '150px', fontSize: '1.5rem' }}
                        onChange={e => onChangeData("month", e.target.value)}
                        value={dataBranch.month}
                        disabled={yearMode}
                    />
                    <label className="d-flex align-items-center ms-2 mb-0" style={{ fontSize: '1.3rem', gap: '8px', color: '#475569' }}>
                        <input
                            type="checkbox"
                            checked={yearMode}
                            onChange={(e) => toggleYearMode(e.target.checked)}
                        />
                        Yearly
                    </label>
                    <button
                        className="btn-load"
                        onClick={handleLoadData}
                    >
                        Load Data
                    </button>
                </div>

                <div className="dashboard-title-group" style={{marginRight:"200px"}}> 
                    <div style={{fontSize:"3rem", fontWeight:"700"}}>{yearMode ? `Yearly Conclusions` : `Monthly Conclusions`} - {headerTargetName}</div>
                    {isAreaSummaryReport && (
                        <div className="area-report-badge">
                            Area Report: {viewRoom.branchName || `${dataBranch.area} (All Branches)`}
                        </div>
                    )}
                    {/* <div className="dashboard-last-update">
                        Last update: {viewRoom.api_lastUpdate || "Today"}
                    </div> */}
                </div>
            </div>

            {viewRoom.content.length > 0 && (
                <>
                    {/* Top Hero Cards Grid (2 Thẻ lớn chứa Doughnut Chart) */}
                    <div className="top-cards-grid">
                        {/* Card 1: Customer Composition */}
                        <div className="exec-card">
                            <span className="card-expand-icon" onClick={() => openReportModal({ type: "customer", title: "Monthly Occupied Nights Breakdown" })}>⤢</span>
                            <div className="card-title">Monthly Occupied Nights Breakdown</div>
                            <div className="hero-card-content">
                                <div className="hero-stats">
                                    <div className="card-value-hero">
                                        {viewRoom.roomTotal || 0} <span> Nights</span>
                                    </div>
                                    {/* <div className="stat-comparison positive">
                                        vs. Target: {viewRoom.api_vsTarget || "+5.0%"} 
                                    </div> */}
                                    <div className="stat-comparison positive">
                                        Capacity Rate: {viewRoom.capacity || 0}% 
                                    </div>

                                    <div className="stat-comparison2 positive" style={{color:'#4285F4'}}>
                                        * Company: {viewRoom.companyTotal || 0} Nights
                                    </div>


                                    <div className="stat-comparison2 positive" style={{color:'#F4B400'}} >
                                        * OTA: {viewRoom.OTATotal || 0} Nights
                                    </div>

                                    <div className="stat-comparison2 positive" style={{color:'#0F9D58'}}>
                                        * Others: {(viewRoom.roomTotal || 0) - ((viewRoom.companyTotal || 0) + (viewRoom.OTATotal || 0))} Nights
                                    </div>

                                </div>
                                <div className="doughnut-wrapper">
                                    <Doughnut data={customerTypeData} options={doughnutOptions} />
                                </div>
                            </div>
                            {previousMonthOccupiedSubText ? (
                                        <div className="sub-stat-text">{previousMonthOccupiedSubText}</div>
                                    ) : null}
                        </div>

                        {/* Card 2: OTA Distribution */}
                        <div className="exec-card">
                            <span className="card-expand-icon" onClick={() => openReportModal({ type: "ota", title: "OTA Channel Distribution" })}>⤢</span>
                            <div className="card-title">OTA Channel Distribution</div>
                            <div className="hero-card-content">
                                <div className="hero-stats">
                                    <div className="card-value-hero">
                                        {viewRoom.OTATotal || 0} <span>nights</span>
                                    </div>
                                    <div className="stat-comparison positive">
                                        Capacity Rate: {(Math.round(((viewRoom.OTATotal || 0) / (viewRoom.roomTotal || 1)) * 10000)) / 100 || 0}% 
                                    </div>

                                    {otaBreakdown.map((item) => (
                                        <div key={item.channel} className="stat-comparison2 positive" style={{ color: item.color }}>
                                            * {formatOtaLabel(item.channel)}: {item.value} Nights
                                        </div>
                                    ))}

                                </div>
                                <div className="doughnut-wrapper">
                                    {otaBreakdown.length > 0 ? (
                                        <Doughnut data={otaChartData} options={doughnutOptions} />
                                    ) : (
                                        <div style={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#64748b',
                                            fontSize: '0.9rem'
                                        }}>
                                            No OTA data
                                        </div>
                                    )}
                                </div>
                            </div>
                            {previousMonthOtaSubText ? (
                                <div className="sub-stat-text">{previousMonthOtaSubText}</div>
                            ) : null}
                        </div>

                      {/* Card 2: Service */}
                        <div className="exec-card">
                            <span className="card-expand-icon" onClick={() => openReportModal({ type: "service", title: "Service On Daily Report" })}>⤢</span>
                            <div className="card-title">Service On Daily Report</div>

                            <div className="service-report-list">
                                {serviceItems.map((item) => {
                                    const percent = maxServiceValue > 0 ? (item.value / maxServiceValue) * 100 : 0;
                                    return (
                                        <div key={item.key} className="service-row">
                                            <div className="service-row-head">
                                                <span className="service-label">{item.label}</span>
                                                <span className="service-value">{item.value}</span>
                                            </div>
                                            <div className="service-track">
                                                <div
                                                    className="service-fill"
                                                    style={{ width: `${percent}%`, backgroundColor: item.color }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                        </div>

                    </div>

                    {/* Middle KPI Cards Grid */}
                    <RoomReportTotalAreaBranchDetailsDay data={viewRoom} onExpandCard={(item) => openReportModal({ type: "kpi", title: item?.title || "KPI Detail", item })} />

                    {/* Bottom Area Comparison Bar Chart */}
                    <div className="d-flex">
                        <div className="exec-card mt-3 mr-2" style={{width:"50%"}}>
                            <span className="card-expand-icon" onClick={() => openReportModal({ type: "areaComparison", title: "Area Capacity Comparison" })}>⤢</span>
                            <div className="card-title">Area Capacity Comparison</div>
                            <div style={{ height: '280px' }}>
                                <Bar
                                    data={areaComparisonData}
                                    options={areaComparisonOptions}
                                />
                            </div>
                        </div>

                        {/* <div className="exec-card mt-3 ml-2" style={{width:"50%"}}>
                            <div className="card-title">Area Capacity Comparison</div>
                            <div style={{ height: '280px' }}>
                                <Bar
                                    data={areaComparisonData}
                                    options={areaComparisonOptions}
                                />
                            </div>
                        </div> */}
                    </div>
                </>
            )}

            {modalInfo && (
                <div className="report-modal-backdrop" onClick={closeReportModal}>
                    <div className="report-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="report-modal-header">
                            <h4>{modalInfo.title || "Report Detail"}</h4>
                            <button className="report-modal-close" onClick={closeReportModal}>×</button>
                        </div>
                        <div className="report-modal-body">
                            {renderModalBody()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RoomReportTotalAreaBranchByGemini;